import type { GetServerSideProps } from "next"
import { usePageProps } from "common/hooks/data/usePageProps"
import { AppProps, getAppServerSideProps } from "common/lib/getAppServerSideProps"
import { BlocksRenderer } from "modules/editor/blocks/BlocksRenderer"
import { type NextPageWithLayout } from "common/types/page"
import { Layout } from "./layout"
import { gql } from "common/lib/gql"
import { ViewPageQuery } from "common/types/graphql"
import { fetchGraphqlQuery } from "common/lib/fetchGraphqlQuery"
import { tcFrontendToken } from "../../k8sSecrets.server"
import { MetaAttributes } from "common/components/MetaAttributes"
import { DEFAULT_HOST, HOME_PAGE_SLUG, pageUrl } from "common/lib/router"
import { tracer } from "common/lib/tracer"
import { useRememberRegion } from "common/hooks/data/useRememberRegion"
import Head from "next/head"

const QUERY = gql`
  query viewPage($regionCode: String!, $pageSlug: String!) {
    currentPage(regionCode: $regionCode, slug: $pageSlug) {
      cacheKey
      pageType
      title
      doc {
        meta
        blocks {
          type
          # props is the block's entire raw hash. Selecting it on the Block
          # interface fetches it for every block type, including the ones that
          # resolve their typed fields out of it and never read the raw copy.
          # GridBlock resolves its children from props, so selecting it there
          # sends the whole grid subtree twice.
          #
          # Request props only for the blocks BlocksRenderer spreads directly,
          # and keep this list in step with that switch.
          ... on BlockquoteBlock {
            props
          }
          ... on BodyBlock {
            props
          }
          ... on CallToActionBlock {
            props
          }
          ... on HeadingBlock {
            props
          }
          ... on IframeBlock {
            props
          }
          ... on ImageBlock {
            props
          }
          ... on LinkBlock {
            props
          }
          ... on ListBlock {
            props
          }
          ... on ParagraphBlock {
            props
          }
          ... on TextBlock {
            props
          }
          ... on VideoBlock {
            props
          }
          ... on VideoWithMetadataBlock {
            props
          }
          ... on GridBlock {
            blocks {
              type
              ... on ContentSummaryBlock {
                type
                id
                url
                tagStyle
                tagLabel
                title
                customTitle
                byline
                contentType
                hideByline
                image {
                  src
                  alt
                  placeholderColor
                  crops {
                    type
                    width
                    height
                    x
                    y
                  }
                }
              }
              ... on CustomUrlBlock {
                type
                title
                href
                byline
                tagLabel
                tagStyle
                imageAlt
                image {
                  id
                  src
                  alt
                  placeholderColor
                  crops {
                    type
                    width
                    height
                    x
                    y
                  }
                }
              }
            }
            slotSizes
            slotTypes
          }
          ... on PartnersBlock {
            type
            props
            partners {
              name
              logoUrl
              path
              type
            }
          }
          ... on MostReadBlock {
            type
            contentSummaries {
              id
              title
              byline
              url
              image {
                src
                placeholderColor
                crops {
                  type
                  width
                  height
                  x
                  y
                }
              }
            }
          }
          ... on EventsBlock {
            type
            events {
              id
              name
              byline
              imageSrc
              trackingUrl
            }
          }
        }
      }
    }
  }
`

export interface ViewPageProps extends AppProps {
  blocks: any[] // Block[]
  meta: {
    title?: string
    description?: string
  }
  title?: string
  canonicalUrl: string
}

export const getServerSideProps: GetServerSideProps<ViewPageProps> = async ({
  query,
  res,
  req,
}) => {
  if (req.method !== "GET" && req.method !== "HEAD") return { notFound: true }

  const appProps = await getAppServerSideProps({ query, res, req })
  const { regionCode, pageSlug } = appProps

  const headers: { Authorization: string; User?: string } = {
    Authorization: `Bearer ${tcFrontendToken()}`,
  }

  const data = await fetchGraphqlQuery<ViewPageQuery>(
    QUERY,
    headers,
    {
      regionCode,
      pageSlug,
    },
    tracer,
  )

  const pageType = data.currentPage?.pageType
  const blocks = data.currentPage?.doc.blocks
  const meta = data.currentPage?.doc.meta
  const title = data.currentPage?.title

  if (!blocks) {
    return { notFound: true }
  }

  // Markers are for whoever asked for them, one cached response would serve them to every reader after.
  if (appProps.phraseIce) {
    res.setHeader("Cache-Control", "private, no-store")
  } else {
    res.setHeader("Cache-Control", "no-store")
    res.setHeader("Surrogate-Control", "max-age=14400") // Pages expires in 4 hours
    res.setHeader("Surrogate-Key", data.currentPage?.cacheKey || "")
  }

  return {
    props: {
      ...appProps,
      pageType,
      blocks,
      meta,
      title,
      editUrl: pageUrl.edit({ regionCode, pageSlug }),
      canonicalUrl: pageUrl.view({
        regionCode,
        pageSlug: pageSlug === HOME_PAGE_SLUG ? "" : pageSlug,
        host: DEFAULT_HOST,
      }),
    },
  }
}

const ViewPage: NextPageWithLayout<ViewPageProps> = () => {
  const { blocks, meta, title, canonicalUrl } = usePageProps<ViewPageProps>()
  useRememberRegion()

  return (
    <>
      <Head>
        <link rel="canonical" href={canonicalUrl} />
      </Head>
      <MetaAttributes
        description={meta?.description}
        title={title}
        ogDescription={meta?.description}
        ogTitle={title}
      />

      <BlocksRenderer blocks={blocks} />
    </>
  )
}

ViewPage.getLayout = (page) => <Layout>{page}</Layout>

export default ViewPage
